import PropTypes from 'prop-types';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useModal } from '../../../../context/ModalContext';
import { useAddContact } from '../../../../hooks/useAddContact';
import { useSelectableUserList } from '../../../../hooks/useSelectableUserList';
import { shouldBlockRemoval } from '../../../../utils/powerLevel';
import logger from '../../../../utils/logger';
import SearchInput from '../../../SearchInput';
import ContactRemovalList from './ContactRemovalList';
import ContactList from '../../../ContactList';
import SettingsToggle from '../../../SettingsModal/SettingsToggle';
import VzdSearchModal from '../../../VzdSearch/VzdSearchModal';
import './ContactsSelector.css';

/**
 * Validates if a string matches the Matrix user ID format
 * @param {string} str - The string to validate
 * @returns {boolean} - True if valid Matrix ID format
 */
const isValidMatrixId = (str) => {
  if (!str || typeof str !== 'string') return false;
  // Matrix IDs must start with @ and contain a :
  // Format: @localpart:homeserver.domain
  return str.startsWith('@') && str.includes(':') && str.length > 3;
};

const ContactsSelector = ({
  hasInvitePermissions = true,
  hasRemovePermissions = true,
  hasRenamePermissions = true,
  hasAvatarPermissions = true,
  originalContacts = [],
  selectedContacts = [],
  onRemoveContact = () => {},
  onToggleContact = () => {},
  onAddExternalUser = null,
  maxSelections = null, // null means unlimited, number limits selections
  showRoomTypeToggle = false,
  isPrivateRoom = false,
  onRoomTypeChange = () => {},
  additionalUsers = [], // Previously selected users not in contact list (cached external users)
  autoFocusSearch = false,
  isBanned = () => false,
  unbanUser = async () => {},
}) => {
  const { t } = useI18n();
  const client = useMatrixClient();
  const { showError, showConfirm } = useModal();
  const { addContact } = useAddContact();
  
  // State for unban modal
  const [isUnbanProcessing, setIsUnbanProcessing] = useState(false);

  // State for VZD search modal
  const [showVzdSearch, setShowVzdSearch] = useState(false);

  // Selected user IDs for VZD modal feedback
  const selectedUserIds = useMemo(
    () => new Set(selectedContacts.map(c => c.id)),
    [selectedContacts],
  );
  
  // Use unified selectable user list (known contacts + search results)
  const { users: baseUsers, searchTerm, setSearchTerm, isSearching } = useSelectableUserList();
  
  // Merge additional users (cached external users) with base users
  const users = useMemo(() => {
    if (!additionalUsers || additionalUsers.length === 0) {
      return baseUsers;
    }
    
    // Deduplicate: don't add if already in base users
    const baseUserIds = new Set(baseUsers.map(u => u.id));
    const uniqueAdditionalUsers = additionalUsers.filter(u => !baseUserIds.has(u.id));
    
    // When searching, filter additional users by search term too
    const filtered = uniqueAdditionalUsers.filter(user => {
      if (!searchTerm.trim()) return true;
      const lowerSearch = searchTerm.toLowerCase();
      const matchesName = user.name.toLowerCase().includes(lowerSearch);
      const matchesId = user.id.toLowerCase().includes(lowerSearch);
      return matchesName || matchesId;
    });
    
    return [...baseUsers, ...filtered];
  }, [baseUsers, additionalUsers, searchTerm]);

  const myUserId = client?.getUserId();
  const currentUser = (myUserId) ? client?.getUser(myUserId) : null;

  // Get current user's power level from originalContacts (which includes powerLevel from useRoom)
  const myPowerLevel = useMemo(() => {
    if (!myUserId || !originalContacts) return 0;
    const me = originalContacts.find(c => c.id === myUserId);
    return me?.powerLevel ?? 0;
  }, [myUserId, originalContacts]);

  // Permissions popup state
  const [showPermissionsPopup, setShowPermissionsPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState('above'); // 'above' or 'below'
  const [isPositioned, setIsPositioned] = useState(false);
  const permissionsRef = useRef(null);
  const popupRef = useRef(null);

  // Calculate popup position based on available space
  useEffect(() => {
    if (showPermissionsPopup && permissionsRef.current && popupRef.current) {
      const iconRect = permissionsRef.current.getBoundingClientRect();
      const popupHeight = popupRef.current.offsetHeight;
      const spaceAbove = iconRect.top;
      const spaceBelow = window.innerHeight - iconRect.bottom;
      const requiredSpace = popupHeight + 12; // popup height + gap

      // Position below if not enough space above, otherwise position above
      if (spaceAbove < requiredSpace && spaceBelow >= requiredSpace) {
        setPopupPosition('below');
      } else {
        setPopupPosition('above');
      }
      
      // Mark as positioned so CSS can show it
      setIsPositioned(true);
    } else if (!showPermissionsPopup) {
      // Reset when popup closes
      setIsPositioned(false);
    }
  }, [showPermissionsPopup]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (permissionsRef.current && !permissionsRef.current.contains(event.target)) {
        setShowPermissionsPopup(false);
      }
    };

    if (showPermissionsPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPermissionsPopup]);
  
  // Check if we've reached the selection limit (only for maxSelections > 1)
  const hasReachedLimit = maxSelections !== null && maxSelections > 1 && selectedContacts.length >= maxSelections;

  // Handle removal from the ContactsRemovalList
  const handleContactRemoval = (contactId) => {
    if (contactId !== currentUser?.id) {
      onRemoveContact(contactId);
    }
  };

  // BUG FIX 6: Improve logic flow for clearer permission and limit checks
  const isDisabled = (contactId) => {
    const isCurrentlySelected = selectedContacts.some(c => c.id === contactId);
    const isOriginalContact = originalContacts.some(c => c.id === contactId);

    // Logic for inviting (toggling ON)
    if (!isCurrentlySelected) {
      // If we don't have invite permissions, we can't add anyone new.
      // However, we can re-add someone who was part of the original list.
      if (!hasInvitePermissions && !isOriginalContact) {
        return true;
      }
      
      // Check if we've reached the limit when trying to add
      if (hasReachedLimit) {
        return true;
      }
    }

    // Logic for removing (toggling OFF)
    if (isCurrentlySelected) {
      // When creating a new room (no originalContacts), always allow deselection
      // This is just candidate selection, not actual room membership
      if (originalContacts.length === 0) {
        return false;
      }

      /**
       * Two-Layer Permission Model for User Removal (Matrix Room v11 Spec §4.5):
       *
       * Layer 1 - Room-level permission (hasRemovePermissions):
       *   Verifies the current user has sufficient power level to kick/remove ANY user
       *   from this room (powerLevel >= room's kick level threshold).
       *   Sourced from useRoomEdit.canRemove prop.
       *
       * Layer 2 - Target-specific permission (shouldBlockRemoval):
       *   Verifies the current user can remove THIS SPECIFIC target user.
       *   Requires target's powerLevel < current user's powerLevel.
       *   Prevents attempting to remove users with equal or higher authority.
       *
       * Both checks are required to comply with Matrix specification and prevent
       * server-rejected operations. Layer 1 ensures room-level authority, Layer 2
       * ensures user-relative authority.
       *
       * IMPORTANT: Power level checks only apply to users who are in the room 
       * (joined or invited). Users who have left can be freely removed from selection
       * and re-invited without power level restrictions.
       */
      const contact = selectedContacts.find(c => c.id === contactId);
      
      // Check if user is in the room (joined or invited, but not left)
      const originalContact = originalContacts.find(c => c.id === contactId);
      const hasLeftRoom = originalContact === undefined;

      // Layer 2: Check if target user's power level blocks removal
      // Skip this check for users who have left the room (they can be re-invited freely)
      // Note: shouldBlockRemoval treats undefined power levels as 0 (default per Matrix spec)
      if (contact && !hasLeftRoom && shouldBlockRemoval(myPowerLevel, contact.powerLevel)) {
        return true;
      }

      // Layer 1: Check if current user has room-level remove permissions
      // Allow un-selecting newly added users (not original members) even without permissions
      if (!hasRemovePermissions && isOriginalContact) {
        return true;
      }
    }

    return false;
  };

  /**
   * Handle inviting a user by Matrix ID - verifies and adds to search results
   */
  const handleInviteById = useCallback(async () => {
    if (!searchTerm || !isValidMatrixId(searchTerm)) return;
    
    try {
      // Verify the user exists without creating a room
      const result = await addContact(searchTerm, false);
      
      if (result && result.success) {
        // User verified - they should appear in the list via search
        // Optionally trigger a search to ensure they appear
        setSearchTerm(searchTerm); // Re-trigger search
      } else if (result && !result.success) {
        showError(
          t('ContactsSelector.inviteError'),
          result.error || t('ContactsSelector.inviteErrorDefault'),
        );
      }
    } catch (err) {
      showError(
        t('ContactsSelector.inviteError'),
        err.message || t('ContactsSelector.inviteErrorDefault'),
      );
    }
  }, [searchTerm, addContact, setSearchTerm, showError, t]);

  /**
   * Handle selecting a user from VZD search results
   */
  const handleVzdUserSelect = useCallback((vzdUser) => {
    if (!vzdUser || !vzdUser.id) {
      logger.warn('[ContactsSelector] VZD user missing id', vzdUser);
      return;
    }
    if (vzdUser.id === myUserId) return;
    // Check if user is already in the contact list
    const existingUser = users.find(u => u.id === vzdUser.id);
    if (existingUser) {
      // User already in list, just toggle them on
      onToggleContact(vzdUser.id);
    } else {
      // Pre-seed external user with practitioner name before toggling
      if (onAddExternalUser) {
        onAddExternalUser({
          id: vzdUser.id,
          name: vzdUser.practitionerName || vzdUser.name || vzdUser.id,
        });
      }
      onToggleContact(vzdUser.id);
    }
    // Keep modal open for multi-selection
  }, [users, onToggleContact, onAddExternalUser, myUserId]);

  /**
   * Handle contact toggle with banned user check
   */
  const handleToggleWithBanCheck = useCallback(async (contactId) => {
    const isCurrentlySelected = selectedContacts.some(c => c.id === contactId);
    
    // If user is being deselected (removed), just call the original toggle
    if (isCurrentlySelected) {
      onToggleContact(contactId);
      return;
    }
    
    // If user is being selected (invited), check if they are banned
    if (isBanned && isBanned(contactId)) {
      // User is banned - show unban modal
      const contact = users.find(u => u.id === contactId) || 
                     additionalUsers.find(u => u.id === contactId);
      const displayName = contact?.name || contactId;
      
      // Show confirmation modal - just unban, don't invite yet
      const confirmed = await new Promise((resolve) => {
        showConfirm(
          t('ChatDetailsController.unbanUserTitle', {}, 'Unban User'),
          t('ChatDetailsController.unbanUserMessage', { displayName }, `${displayName} is banned from this room. You need to unban ${displayName} first before inviting again`),
          () => resolve(true),  // onConfirm
          () => resolve(false),  // onCancel
          t('ChatDetailsController.unbanButton', {}, 'Unban'), // Custom confirm button text
          t('cancel', {}, 'Cancel'), // Custom cancel button text
        );
      });
      
      if (!confirmed) {
        return;
      }
      
      // User confirmed - unban only
      setIsUnbanProcessing(true);
      try {
        await unbanUser(contactId);
        logger.info('User unbanned successfully, adding to selection:', contactId);
      } catch (error) {
        logger.error('Failed to unban user:', error);
        showError(
          t('ChatDetailsController.unbanErrorTitle', {}, 'Unban Failed'),
          error.message || t('ChatDetailsController.unbanErrorMessage', {}, 'Failed to unban user. Please try again.'),
        );
        return;
      } finally {
        setIsUnbanProcessing(false);
      }

      // After successful unban, toggle the contact to add them to selection
      try {
        onToggleContact(contactId);
      } catch (error) {
        logger.error('Failed to add user to selection after unban:', error);
        // User was unbanned successfully, just failed to add to selection - no need to show error
      }
      return;
    }
    
    // Normal toggle (not banned)
    onToggleContact(contactId);
  }, [selectedContacts, isBanned, unbanUser, onToggleContact, users, additionalUsers, showConfirm, showError, t]);

  // Show invite button when search term is a valid Matrix ID not in the list
  const isInUserList = users.some(user => user.id.toLowerCase() === searchTerm.toLowerCase());
  const showInviteButton = searchTerm && isValidMatrixId(searchTerm) && !isInUserList;

  // Build list of permission restrictions
  const permissionRestrictions = useMemo(() => {
    const restrictions = [];
    if (!hasInvitePermissions) restrictions.push(t('ChatEdit.noInvitePermissionsTipMessage'));
    if (!hasRemovePermissions) restrictions.push(t('ChatEdit.noRemovePermissionsTipMessage'));
    if (!hasRenamePermissions) restrictions.push(t('ChatEdit.noRenamePermissionsTipMessage'));
    if (!hasAvatarPermissions) restrictions.push(t('ChatEdit.noAvatarPermissionsTipMessage'));
    return restrictions;
  }, [hasInvitePermissions, hasRemovePermissions, hasRenamePermissions, hasAvatarPermissions, t]);

  const hasRestrictions = permissionRestrictions.length > 0;

  return (
    <div className="contacts-selector">
      <div className="contacts-selector-controls">
        <div className="contacts-selector-permissions-line">
          <span>{t('ContactsSelector.myPermissions')}</span>
          <div className="contacts-selector-permissions-icon-wrapper" ref={permissionsRef}>
            <ion-icon 
              name="information-circle"
              onClick={() => setShowPermissionsPopup(!showPermissionsPopup)}
            />
            {showPermissionsPopup && (
              <div 
                ref={popupRef}
                className={`contacts-selector-permissions-popup ${popupPosition} ${isPositioned ? 'positioned' : ''}`}
              >
                <div className="contacts-selector-permissions-popup-header">
                  {t('ContactsSelector.myPermissions')}
                </div>
                <div className="contacts-selector-permissions-popup-content">
                  {hasRestrictions ? (
                    <ul>
                      {permissionRestrictions.map((restriction, index) => (
                        <li key={index}>{restriction}</li>
                      ))}
                    </ul>
                  ) : (
                    <span>{t('ContactsSelector.noRestrictions')}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="contacts-selector-search-container">
          <SearchInput
            id="contacts-selector-search"
            value={searchTerm}
            placeholderValue={t('SearchInput.searchContactsId')}
            onChange={setSearchTerm}
            onClearClick={() => setSearchTerm('')}
            autoFocus={autoFocusSearch}
          />
          {showInviteButton && (
            <div
              className="contacts-selector-invite-button"
              onClick={handleInviteById}
            >
              <span className="material-icons">email</span>
              {t('ContactsSelector.inviteById', { id: searchTerm })}
            </div>
          )}
          <div
            className="contacts-selector-vzd-button"
            onClick={() => setShowVzdSearch(true)}
          >
            <img src="/images/svg/search-healthcare.svg" alt="" />
            {t('ContactsSelector.searchVzdDirectory')}
          </div>
        </div>
        {showRoomTypeToggle && (
          <div className="contacts-selector-room-type">
            <SettingsToggle
              label={t('ChatEdit.privateRoom')}
              description={t('ChatEdit.privateRoomDescription')}
              checked={isPrivateRoom}
              onChange={onRoomTypeChange}
            />
          </div>
        )}
        <span className="contacts-selector-instructions">
          {maxSelections === 1
            ? t('ContactsSelector.selectOneContactMessage')
            : t('ChatEdit.addGuestsTipMessage')
          }
        </span>
        <ContactRemovalList
          contacts={selectedContacts}
          onContactRemove={handleContactRemoval}
          isRemovable={(contactId) => !isDisabled(contactId)}
        />
      </div>
      <div className="contacts-selector-list-container">
        <ContactList
          contacts={users}
          isSelectList={true}
          selectedIds={selectedContacts.map(c => c.id)}
          onSelectContact={handleToggleWithBanCheck}
          isLoading={isSearching || isUnbanProcessing}
          isToggleList={true}
          isToggled={(id) => selectedContacts.some(c => c.id === id)}
          disabled={isDisabled}
        />
      </div>
      <VzdSearchModal
        isOpen={showVzdSearch}
        onClose={() => setShowVzdSearch(false)}
        onSelectUser={handleVzdUserSelect}
        selectedUserIds={selectedUserIds}
      />
    </div>
  );
};

ContactsSelector.propTypes = {
  hasInvitePermissions: PropTypes.bool,
  hasRemovePermissions: PropTypes.bool,
  hasRenamePermissions: PropTypes.bool,
  hasAvatarPermissions: PropTypes.bool,
  originalContacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    powerLevel: PropTypes.number,
  })),
  selectedContacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    powerLevel: PropTypes.number,
  })),
  onRemoveContact: PropTypes.func,
  onToggleContact: PropTypes.func,
  onAddExternalUser: PropTypes.func,
  maxSelections: PropTypes.number,
  showRoomTypeToggle: PropTypes.bool,
  isPrivateRoom: PropTypes.bool,
  onRoomTypeChange: PropTypes.func,
  additionalUsers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string,
  })),
  autoFocusSearch: PropTypes.bool,
  isBanned: PropTypes.func,
  unbanUser: PropTypes.func,
};

export default ContactsSelector;
